import { z } from "zod"
import { isPast } from "date-fns"
import { Organization, Student, User, TestSubject } from "db"

export const OrganizationInput = z.object({
  id: z.number().optional(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string().email(),
  address1: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  contactIsGuard: z.boolean().optional().default(true),
})
export type OrganizationInputType = z.infer<typeof OrganizationInput>

export type OrganizationData = Organization & {
  user: User | null
  students: Student[]
}

export const StudentInput = z
  .object({
    id: z.number().optional(),
    firstName: z.string().nonempty({ message: "Required" }),
    lastName: z.string().nonempty({ message: "Required" }),
    birthdate: z.date().refine((val) => isPast(val), {
      message: "Must be a real birthdate in the past",
    }),
    email: z
      .string()
      .optional()
      .nullable()
      .refine((value) => !value?.includes("+"), {
        message: "The email cannot contain a (+) symbol ",
      }),
    gender: z.string().optional().nullable().default(""),
    ethnicity: z.string().optional().nullable().default(""),
    freeOReduced: z.boolean().optional().nullable(),
    gradeTestLevelStr: z.string().nonempty({ message: "Required" }),
    textToSpeech: z.boolean().optional(),
    accommodations: z.string().optional().nullable(),
    tests: z.object({
      reading: z.string().optional(),
      math: z.string().optional(),
      language: z.boolean().optional().nullable(),
      science: z.string().optional().nullable(),
      fluency: z.string().optional().nullable(),
      mindprint: z.string().optional().nullable(),
      sp_reading: z.string().optional().nullable(),
      sp_math: z.string().optional().nullable(),
      star: z.string().optional().nullable(),
    }),
    testAboveLevel: z.boolean().optional(),
    hasBookedLanguageTest: z.boolean(),
    hasBookedScienceTest: z.boolean(),
    hasBookedSpReading: z.boolean().optional(),
    hasBookedSpMath: z.boolean().optional(),
    isEditingOrder: z.boolean(),
    isEnrolledInFluency: z.boolean().optional(),
  })
  .refine(
    (data) => {
      return data.tests.fluency && !data.email ? false : true
    },
    {
      path: ["email"],
      message: "Required for test",
    }
  )
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.hasBookedLanguageTest) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.tests.language || false
    },
    {
      path: ["tests", "language"],
      message: "To remove language from an existing order, contact ellen@homeschoolboss.com",
    }
  )
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.hasBookedScienceTest) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.tests.science || false
    },
    {
      path: ["tests", "science"],
      message: "To remove science from an existing order, contact ellen@homeschoolboss.com",
    }
  )
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.hasBookedSpReading) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.tests.sp_reading || false
    },
    {
      path: ["tests", "sp_reading"],
      message: "To remove spanish reading from an existing order, contact ellen@homeschoolboss.com",
    }
  )
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.hasBookedSpMath) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.tests.sp_math || false
    },
    {
      path: ["tests", "sp_math"],
      message: "To remove spanish math from an existing order, contact ellen@homeschoolboss.com",
    }
  )

export type StudentInputType = z.infer<typeof StudentInput>

const Subject = z.nativeEnum(TestSubject)

export const BookingInputData = z.object({
  id: z.number().optional(),
  startAt: z.date().optional().nullable(),
  subject: Subject.optional().nullable(),
})
export type BookingInputDataType = z.infer<typeof BookingInputData>

export const BookingsInput = z.object({
  reading: BookingInputData,
  math: BookingInputData,
  language: BookingInputData,
  science: BookingInputData,
  sp_reading: BookingInputData,
  sp_math: BookingInputData,
})
export type BookingsInputType = z.infer<typeof BookingsInput>

export const Addons = z.object({
  resultsConsultation: z.boolean().optional(),
})
export type AddonsType = z.infer<typeof Addons>

export const StartNewBookingInput = z.object({
  email: z.string().email().optional(),
  agreeToTestInUS: z.literal(true),
  bookingType: z.union([
    z.literal("homeschool"),
    z.literal("group"),
    z.literal("privateschool"),
    z.literal("readingfluency"),
    z.literal("mindprint"),
    z.literal("star"),
  ]),
})
export type StartNewBookingInputType = z.infer<typeof StartNewBookingInput>

export const StartNewGroupBookingInput = z.object({
  email: z.string().email().optional(),
  groupUuid: z.string(),
  groupId: z.number(),
})

export type StartNewGroupBookingInputType = z.infer<typeof StartNewGroupBookingInput>

export type SubmitPrivateSchoolBookingInputType = z.infer<typeof RequestPrivateSchoolBooking>
export const RequestPrivateSchoolBooking = z.object({
  schoolName: z.string(),
  contactName: z.string(),
  contactTitle: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string(),

  // gradeLevels: z.array(z.string()),
  gradeK: z.boolean().optional().default(false),
  grade1: z.boolean().optional().default(false),
  grade2: z.boolean().optional().default(false),
  grade3: z.boolean().optional().default(false),
  grade4: z.boolean().optional().default(false),
  grade5: z.boolean().optional().default(false),
  grade6: z.boolean().optional().default(false),
  grade7: z.boolean().optional().default(false),
  grade8: z.boolean().optional().default(false),
  grade9: z.boolean().optional().default(false),
  grade10: z.boolean().optional().default(false),
  grade11: z.boolean().optional().default(false),
  grade12: z.boolean().optional().default(false),

  numOfGradeKStudents: z.number().optional(),
  numOfGrade1Students: z.number().optional(),
  numOfGrade2Students: z.number().optional(),
  numOfGrade3Students: z.number().optional(),
  numOfGrade4Students: z.number().optional(),
  numOfGrade5Students: z.number().optional(),
  numOfGrade6Students: z.number().optional(),
  numOfGrade7Students: z.number().optional(),
  numOfGrade8Students: z.number().optional(),
  numOfGrade9Students: z.number().optional(),
  numOfGrade10Students: z.number().optional(),
  numOfGrade11Students: z.number().optional(),
  numOfGrade12Students: z.number().optional(),

  studentsTesting: z.string(),

  // subjects: z.array(z.string()),
  reading: z.boolean().optional().default(false),
  math: z.boolean().optional().default(false),
  science: z.boolean().optional().default(false),
  language: z.boolean().optional().default(false),

  // seasons: z.array(z.string()),

  fall: z.boolean().optional().default(false),
  winter: z.boolean().optional().default(false),
  spring: z.boolean().optional().default(false),

  otherQuestions: z.string(),
})

export const SubmitGroupStep1 = z
  .object({
    orderId: z.string(),
    isEditingOrder: z.boolean(),
    organization: z.object({
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string(),
    }),
    orderGroup: z.object({
      name: z.string(),
      size: z.string(),
      website: z.string().optional().nullable(),
      canListWebsite: z.boolean().optional(),
      includeLanguage: z.boolean().optional(),
      includeScience: z.boolean().optional(),
      hasIncludedLanguage: z.boolean(),
      hasIncludedScience: z.boolean(),
    }),
  })
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.orderGroup.hasIncludedLanguage) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.orderGroup.includeLanguage || false
    },
    {
      path: ["orderGroup", "includeLanguage"],
      message: "To remove language from an existing order, contact ellen@homeschoolboss.com",
    }
  )
  .refine(
    (data) => {
      if (!data.isEditingOrder || !data.orderGroup.hasIncludedScience) return true

      // Don't allow dropping this test
      // Test was selected, so only valid if it stays selected
      return data.orderGroup.includeScience || false
    },
    {
      path: ["orderGroup", "includeScience"],
      message: "To remove science from an existing order, contact ellen@homeschoolboss.com",
    }
  )
export type SubmitGroupStep1Type = z.infer<typeof SubmitGroupStep1>

export const SubmitHomeschoolStep1 = z.object({
  orderId: z.string(),
  students: z.array(StudentInput),
  hostname: z.string(),
})
export type SubmitHomeschoolStep1Type = z.infer<typeof SubmitHomeschoolStep1>

export const SubmitGroupStep3 = z.object({
  orderId: z.string(),
  password: z.string().optional(),
  agreeToTerms: z.literal(true),
  draftOrder: z.optional(z.number()),
})
export type SubmitGroupStep3Type = z.infer<typeof SubmitGroupStep3>

export const SubmitFluencyStep1 = z.object({
  orderId: z.string(),
  students: z.array(StudentInput),
  hostname: z.string(),
})
export type SubmitFluencyStep1Type = z.infer<typeof SubmitFluencyStep1>

export const SubmitPaymentInput = z.object({
  orderId: z.string().optional(),
  org: OrganizationInput,
  stripeSourceId: z.string().nullable(),
  paymentIntentId: z.optional(z.string().nullable()),
  agreeToTerms: z.literal(true),
  coupons: z.array(z.string()),
  vouchers: z.array(z.string()),
  password: z.string().optional(),
  chargeType: z.union([z.literal("once"), z.literal("month"), z.literal("year")]),
  optInToNewsLetter: z.optional(z.boolean()),
  smsOptIn: z.optional(z.boolean()),
  draftOrder: z.optional(z.number()),
})
export type SubmitPaymentInputType = z.infer<typeof SubmitPaymentInput>
export const SubmitGroupPaymentInput = SubmitPaymentInput.extend({
  groupUuid: z.string(),
})
export type SubmitGroupPaymentInputType = z.infer<typeof SubmitGroupPaymentInput>

export const SubmitBookingInput = z.object({
  orderId: z.string(),
  bookings: BookingsInput,
  timezone: z.string(),
  season: z.any().optional(),
})
export type SubmitBookingInputType = z.infer<typeof SubmitBookingInput>

export const GetUserByIdInput = z.object({
  id: z.number().optional().nullable(),
})
export type GetUserByIdInputType = z.infer<typeof GetUserByIdInput>

export const SubmitBatchRegistrationStep1 = z.object({
  newUserData: z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
    address1: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    email: z.string(),
  }),
})
export type SubmitBatchRegistrationStep1Type = z.infer<typeof SubmitBatchRegistrationStep1>

export const SubmitBatchRegistrationStep2 = z.object({
  orgId: z.number(),
  students: z.array(
    z.object({
      id: z.number(),
      grade: z.number(),
      schoolId: z.number(),
      organizationId: z.number(),
      gradeTestLevelStr: z.string(),
      textToSpeech: z.string(),
      email: z.string().email().optional().nullable(),
    })
  ),
  selectedTests: z.object({
    reading: z.boolean().optional(),
    math: z.boolean().optional(),
    language: z.boolean().optional(),
    science: z.boolean().optional(),
    fluency: z.boolean().optional(),
    sp_reading: z.boolean().optional(),
    sp_math: z.boolean().optional(),
    star: z.boolean().optional(),
  }),
})
export type SubmitBatchRegistrationStep2Type = z.infer<typeof SubmitBatchRegistrationStep2>

export const SubmitBatchRegistrationStep3 = z.object({
  orgId: z.number(),
  orderId: z.string(),
  bookings: BookingsInput,
  timezone: z.string(),
  season: z.any().optional(),
})
export type SubmitBatchRegistrationStep3Type = z.infer<typeof SubmitBatchRegistrationStep3>

export const Coupon = z.object({
  id: z.number(),
  code: z.string(),
  enabled: z.boolean(),
  value: z.number(),
  type: z.string(),
})

export const Voucher = z.object({
  id: z.number(),
  code: z.string(),
  email: z.string(),
  initialValue: z.number(),
  remainingValue: z.number(),
})

const mindprintPasswordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{5,}$/
const eightYearsAgo = new Date()
eightYearsAgo.setFullYear(eightYearsAgo.getFullYear() - 8)

export const StudentMindprintInput = z.object({
  id: z.number().optional(),
  firstName: z.string().nonempty({ message: "Required" }),
  lastName: z.string().nonempty({ message: "Required" }),
  birthdate: z
    .date()
    .refine((val) => isPast(val), {
      message: "Must be a real birthdate in the past",
    })
    .refine((val) => val <= eightYearsAgo, {
      message: "Must be at least 8 years old",
    }),
  email: z
    .string()
    .nonempty({ message: "Required" })
    .refine((value) => !value?.includes("+"), {
      message: "The email cannot contain a (+) symbol ",
    }),
  gender: z.string().nonempty({ message: "Required" }),
  // ethnicity: z.string().nonempty({ message: "Required" }),
  // freeOReduced: z.string().nonempty({ message: "Required" }),
  gradeTestLevelStr: z.string().nonempty({ message: "Required" }),
  tests: z.object({
    mindprint: z.boolean().optional(),
  }),
  isEditingOrder: z.boolean(),
})
export type StudentMindprintInputType = z.infer<typeof StudentMindprintInput>

export const MiscTestStudentInput = z
  .object({
    id: z.number().optional(),
    firstName: z.string().nonempty({ message: "Required" }),
    lastName: z.string().nonempty({ message: "Required" }),
    birthdate: z.date().refine((val) => isPast(val), {
      message: "Must be a real birthdate in the past",
    }),
    email: z
      .string()
      .optional()
      .nullable()
      .refine((value) => !value?.includes("+"), {
        message: "The email cannot contain a (+) symbol ",
      }),
    gender: z.string().nonempty({ message: "Required" }),
    gradeTestLevelStr: z.string().nonempty({ message: "Required" }),
    tests: z.object({
      mindprint: z.boolean().optional(),
      fluency: z.enum(["RFA", "RFD"]).or(z.literal("")).optional(),
      star: z.boolean().optional(),
    }),
    isEditingOrder: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.tests.mindprint) {
      const birthdate = data.birthdate
      if (!(birthdate <= eightYearsAgo)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Student must be at least 8 years old for Mindprint",
          path: ["birthdate"],
        })
      }
    }
  })
export type MiscTestStudentInputType = z.infer<typeof MiscTestStudentInput>

export const SubmitTestRegistrationStep1 = z.object({
  orderId: z.string(),
  students: z.array(MiscTestStudentInput),
  hostname: z.string(),
  guest: z.string().optional(),
})
export type SubmitTestRegistrationStep1Type = z.infer<typeof SubmitTestRegistrationStep1>

export const SubmitMindprintStep1 = z.object({
  orderId: z.string(),
  students: z.array(StudentMindprintInput),
  hostname: z.string(),
})
export type SubmitMindprintStep1Type = z.infer<typeof SubmitMindprintStep1>

export const RescheduleStudentsFormInput = z
  .object({
    bookingId: z.number(),
    selectedStudents: z.record(z.boolean()),
    newDate: z.date(),
    timezone: z.string(),
  })
  .refine((data) => Object.values(data.selectedStudents || {}).some((v) => v), {
    path: ["selectedStudents"],
    message: "At least one student must be selected",
  })
export type RescheduleStudentsFormType = z.infer<typeof RescheduleStudentsFormInput>
